import { reactive, readonly, ref } from '@vue/composition-api'

import { Offer } from '@/resources'
import {
  BASE_MAP_OPTIONS,
  FLY_DURATION,
  bindPopup,
  fitToPositions,
  icons,
  loadGoogleMaps,
  popupAnchorPoint
} from '@/services/GoogleMaps'

import useStore from '@/composables/useStore'

/**
 * Everything the two offer maps do, so they are left with a container and a
 * size. They show the same thing from different sources — the current offer in
 * the store for one, an offer prop for the other — which is the only reason
 * they are still two components.
 *
 * Google draws imperatively, so where the Leaflet version declared markers and
 * a polyline in its template, this creates them and hands back the handles
 * needed to take them off again.
 */
export default function useMap () {
  const store = useStore()

  const defaultMapData = {
    zoom: 5,
    maxZoom: 14,
    minZoom: 4,
    center: { lat: 48.864716, lng: 2.349014 }
  }

  const mapData = readonly(Object.assign({}, defaultMapData))
  const map = ref(null)
  const itinerary = reactive({
    timeout: null,
    visible: false,
    path: null,
    style: {
      color: '#287696',
      opacity: 1,
      weight: 3
    }
  })

  /** The overlays currently on the map, kept so they can be removed. */
  let markers = []
  let route = null

  function updateItineraryCoords (coordinates) {
    itinerary.path = coordinates
      .map(location => ({
        lat: location[1],
        lng: location[0]
      }))

    drawItinerary()

    setTimeout(() => {
      zoomToMarkers()
    }, 1500)
  }

  /**
   * Returns the itinerary from the API or the store, if previously fetched.
   * @function retrieveItinerary
   * @param {object} offer - The offer object
   */
  const retrieveItinerary = offer => {
    const currentOffer = store.value.getters['offers/getCurrentOffer']
    const itineraries = store.value.getters['offers/getOffersItineraries']
    const exists = itineraries.find(e => e.uuid === offer.uuid)

    removeLine()

    if (exists) {
      updateItineraryCoords(exists.itinerary.coordinates)
    } else {
      /**
       * Retrive the offer only if the offer is available
       */
      if (!(typeof offer.available_for_booking !== 'undefined' && offer.available_for_booking === false)) {
        Offer.travel({
          cid: store.value.getters['auth/getCid'],
          oid: offer.uuid
        })
          .then((res) => {
            if (res.data.itinerary) {
              const { itinerary, distance, duration } = res.data

              /**
               * Update the travel informations
               */
              store.value.dispatch('offers/setCurrentOffer', {
                ...currentOffer,
                travel: {
                  distance,
                  duration
                }
              })

              store.value.dispatch('offers/pushItinerary', {
                uuid: offer.uuid,
                itinerary
              })

              /**
               * Force the itinerary change only if the offer didn't change during
               * the request.
               */
              if (currentOffer && currentOffer.uuid === offer.uuid) {
                updateItineraryCoords(itinerary.coordinates)
              }
            }
          })
          .catch((err) => {
            console.log('Error occured while retrieving this itinerary', err)
          })
      }
    }
  }

  /**
   * Remove the line itinerary from the map
   * @function removeLine
   */
  const removeLine = () => {
    itinerary.visible = false
    itinerary.path = null
    if (route) {
      route.setMap(null)
      route = null
    }
    if (itinerary.timeout) {
      clearTimeout(itinerary.timeout)
      itinerary.timeout = null
    }
  }

  /**
   * Creates the map in its container, once the Google script is there.
   * @function createMap
   * @param {HTMLElement} container
   * @param {object} [options] - Anything the map overrides, such as the wheel zoom
   * @returns {Promise<any>} map
   */
  async function createMap (container, options = {}) {
    const maps = await loadGoogleMaps()

    /**
     * The dialogs these maps live in can close while the script loads, and a
     * map built into a detached element never draws.
     */
    if (!container) return null

    map.value = new maps.Map(container, {
      ...BASE_MAP_OPTIONS,
      zoom: mapData.zoom,
      maxZoom: mapData.maxZoom,
      minZoom: mapData.minZoom,
      center: mapData.center,
      scrollwheel: false,
      ...options
    })

    return map.value
  }

  /**
   * Drops the pickup & delivery markers of an offer, each with the popup its
   * address opens.
   * @function drawOffer
   * @param {object} offer
   */
  function drawOffer (offer) {
    removeMarkers()

    if (!map.value || !hasLocation(offer)) return

    markers = ['pickup', 'delivery'].map(direction => {
      const { address } = offer[direction]
      const marker = new window.google.maps.Marker({
        map: map.value,
        position: address.location,
        icon: direction === 'pickup' ? icons.pickup('in_progress') : icons.delivery('in_progress'),
        anchorPoint: popupAnchorPoint()
      })

      bindPopup(map.value, marker, formatAddress(address, direction))

      return marker
    })
  }

  /**
   * Draws the itinerary line, once there is one to draw. It stays hidden until
   * the camera has finished framing the markers, as it did with Leaflet: a line
   * redrawn at every animation frame reads as flicker.
   * @function drawItinerary
   */
  function drawItinerary () {
    if (route) {
      route.setMap(null)
      route = null
    }

    if (!map.value || !itinerary.path) return

    route = new window.google.maps.Polyline({
      map: map.value,
      path: itinerary.path,
      strokeColor: itinerary.style.color,
      strokeOpacity: itinerary.style.opacity,
      strokeWeight: itinerary.style.weight,
      visible: itinerary.visible
    })
  }

  /**
   * @function removeMarkers
   */
  function removeMarkers () {
    markers.forEach(marker => marker.setMap(null))
    markers = []
  }

  /**
   * @function hasLocation
   * @param {object} offer
   * @returns {boolean}
   */
  function hasLocation (offer) {
    return !!(offer && offer.pickup && offer.delivery &&
      offer.pickup.address.location && offer.delivery.address.location)
  }

  /**
   * Zoom to the marker's (or itinerary if available) boundaries
   * @function zoomToMarkers
  */
  function zoomToMarkers () {
    if (!map.value || !markers.length) return

    try {
      fitToPositions(map.value, markers.map(marker => marker.getPosition()))

      itinerary.timeout = setTimeout(() => {
        itinerary.visible = true
        if (route) route.setVisible(true)
      }, FLY_DURATION * 1000)
    } catch (e) {
      console.error('Could not zoom to the marker boundaries.', e)
    }
  }

  /**
   * Takes every overlay off the map, for a component going away.
   * @function clear
   */
  function clear () {
    removeMarkers()
    removeLine()
  }

  /**
   * Returns a formatted address string
   * @function formatAddress
   * @param {object} address - Address object from a single offer
   * @param {string} direction
   * @returns {string} formattedAddress
   */
  const formatAddress = (address, direction) => {
    return `
      <div class="header ${direction}">
        <b>${address.city}</b><br />
        ${address.postal_code}, ${address.country}
      </div>
    `
  }

  return {
    map,
    mapData,
    itinerary,
    createMap,
    drawOffer,
    drawItinerary,
    clear,
    formatAddress,
    zoomToMarkers,
    hasLocation,
    retrieveItinerary
  }
}
